import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { Observable, catchError, of, switchMap, tap, throwError } from 'rxjs';
import { DashbordService } from '../dashbord/dashbord.service';
import { LocalStorageService } from '../local/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private _authenticated = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _localService: LocalStorageService,
        private _router: Router,
        private _dashbordService: DashbordService,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Setter & getter for access token
     */
    get accessToken(): string {
        return this._localService.getItem('accessToken') ?? '';
    }

    set accessToken(token: string) {
        this._localService.setItem('accessToken', token);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(`${environment.apiBaseUrl}/auth/forgot-password`, { email });
    }

    updatePassword(data): Observable<any> {
        return this._httpClient.post(`${environment.apiBaseUrl}/auth/change-password`, data);
    }

    userLogout(id): Observable<any> {
        return this._httpClient.get(`${environment.apiBaseUrl}/auth/user-logout/${id}`);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, token: string): Observable<any> {
        return this._httpClient.post(`${environment.apiBaseUrl}/auth/reset-password`, { password }, { params: { token } });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this.accessToken) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(`${environment.apiBaseUrl}/auth/login`, credentials).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.tokens.access.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token and user details from the local storage
        this._localService.removeItems(['accessToken', 'user', 'navigationMenu', 'permission']);

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post(`${environment.apiBaseUrl}/auth/register`, user);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        if (this.accessToken) {
            return of(true);
        }

        // Check the access token expire date
        // if (AuthUtils.isTokenExpired(this.accessToken)) {
        //     return of(false);
        // }
        // this._userService.user = this._localService.getItem('user') || {};
        // return of(true);
    }

    _checkToken(): Observable<any> {
        // Check the authentication status

        return this.check()
            .pipe(
                switchMap((authenticated) => {
                    // If the user is not authenticated...
                    if (!authenticated) {
                        // Redirect to the sign-in page
                        this._router.navigate(['sign-in']);
                        // Prevent the access
                        return of(false);
                    }
                    this._dashbordService.dashboardDetails().subscribe();
                    // Allow the access
                    return of(true);
                })
            );
    }
}
